import {ColumnSpec} from '@xh/hoist/cmp/grid';
import {HoistModel, LoadSpec, lookup, managed, PlainObject, XH} from '@xh/hoist/core';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {action, observable, observableRef, runInAction, when} from '@xh/hoist/mobx';
import {SECONDS} from '@xh/hoist/utils/datetime';
import {isEqual, startCase} from 'lodash';
import {ConfigurableChartModel} from '../../../cmp/chartSpec/ConfigurableChartModel';
import {ChartSpec} from '../../../cmp/chartSpec/ChartSpecTypes';
import {ConfigurableGridModel} from '../../../cmp/gridSpec/ConfigurableGridModel';
import {GridSpec} from '../../../cmp/gridSpec/GridSpecTypes';
import {formatValue} from '../../../cmp/spec/Formats';
import {ResearchDataset, ResearchDataSource, ResearchWidgetKind, ResearchWidgetState} from '../../../data/ResearchTypes';
import {
    chartCapabilitiesFor,
    defaultChartSpec,
    defaultGridSpec,
    fieldCatalogFor,
    researchGridCapabilities,
    storeConfigFor,
    transposeRows,
    valueFormat
} from '../ResearchDatasets';
import {refineInstruction} from '../dashboardSpec/WidgetRefine';
import {ResearchTabModel} from '../ResearchTabModel';
import {ResearchDataDialogModel} from './ResearchDataDialogModel';

type WidgetContent = ConfigurableGridModel | ConfigurableChartModel;

/** How long a widget being refined waits for its rows. */
const REFINE_WAIT = 30 * SECONDS;

/**
 * A research widget - a grid or a chart over any research dataset, for the tab's ticker. Its saved state
 * is a data source plus a GridSpec / ChartSpec, so the same widget serves every dataset: the user picks
 * the data, and the configurator or the AI assistant shapes the spec.
 *
 * The state lives in the widget's DashViewModel. It can change from outside (another dashboard is
 * selected, the AI rebuilds the dashboard), so the grid / chart model is rebuilt whenever the state no
 * longer matches what is shown.
 */
export class ResearchWidgetModel extends HoistModel {
    @lookup(DashViewModel) viewModel: DashViewModel;
    @lookup(() => ResearchTabModel) tabModel: ResearchTabModel;

    @managed dataDialogModel = new ResearchDataDialogModel(source => this.setSource(source));

    kind: ResearchWidgetKind;

    @observableRef accessor source: ResearchDataSource = null;
    /** The grid or chart for the source - null while there is nothing to show. */
    @observableRef accessor content: WidgetContent = null;
    /** Why there is nothing to show, when the source can't be shown at all. */
    @observable accessor problem: string = null;
    /** The content whose rows have been loaded - once it is the current content, the widget shows its data. */
    @observableRef private accessor loadedContent: WidgetContent = null;

    private dataset: ResearchDataset;

    get gridModel(): ConfigurableGridModel {
        return this.content instanceof ConfigurableGridModel ? this.content : null;
    }

    get chartModel(): ConfigurableChartModel {
        return this.content instanceof ConfigurableChartModel ? this.content : null;
    }

    override onLinked(): void {
        const {viewModel} = this;
        this.kind = this.componentProps.kind;

        this.tabModel.registerWidget(viewModel.id, this);

        viewModel.extraMenuItems = [
            ...viewModel.extraMenuItems,
            {icon: Icon.database(), text: 'Data...', actionFn: () => this.openDataDialog()},
            {icon: Icon.gear(), text: 'Configure', actionFn: () => this.content?.openConfigurator(), prepareFn: item => (item.disabled = !this.content)}
        ];

        this.addReaction(
            {
                track: () => viewModel.viewState,
                run: () => this.syncWithViewState(),
                fireImmediately: true
            },
            {
                track: () => [this.content, this.tabModel.readyTicker, this.tabModel.dataRevision],
                run: () => this.loadAsync(),
                fireImmediately: true
            }
        );
    }

    openDataDialog() {
        this.dataDialogModel.open(this.kind, this.source);
    }

    /** Show other data - the spec starts over from the default for the new source. */
    setSource(source: ResearchDataSource) {
        const {viewModel} = this,
            prevTitle = XH.researchService.getDataset(this.source?.dataset)?.title,
            dataset = XH.researchService.getDataset(source.dataset);

        // Keep a title the user chose; replace one that just named the kind or the previous dataset
        if (dataset && [startCase(this.kind), prevTitle].includes(viewModel.title)) viewModel.title = dataset.title;

        const state: ResearchWidgetState = {source};
        viewModel.setViewState(state);
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const {content, source} = this,
            ticker = this.tabModel.readyTicker;
        if (!content) return;

        let rows: PlainObject[] = [];
        if (ticker) {
            try {
                rows = await XH.researchService.queryAsync(source, ticker);
            } catch (e) {
                if (loadSpec.isStale) return;
                XH.handleException(e, {showAlert: false, title: `Error loading ${this.dataset.title}`});
            }
        }
        if (loadSpec.isStale || content !== this.content) return;

        const {gridModel, chartModel} = this;
        if (chartModel) chartModel.setRows(rows);
        else gridModel.gridModel.loadData(source.transpose ? transposeRows(this.dataset, rows, source.fields) : rows);
        runInAction(() => (this.loadedContent = content));
    }

    /**
     * Have the widget's own assistant finish it for a purpose, once its rows have loaded - so the
     * assistant sees them. Returns what was done, in a line.
     */
    async refineAsync(purpose: string): Promise<string> {
        const {title} = this.viewModel;
        try {
            await when(() => !!this.problem || (!!this.content && this.loadedContent === this.content), {timeout: REFINE_WAIT});
        } catch {
            return `${title}: not refined - its data didn't load in time`;
        }
        if (this.problem || !this.content) return `${title}: not refined - ${this.problem ?? 'it shows nothing'}`;

        const turn = await this.content.assistantModel.askAsync(refineInstruction(this.kind, purpose));
        if (turn.error) return `${title}: not refined - ${turn.error}`;
        return turn.appliedLabel ? `${title}: ${turn.appliedLabel}` : `${title}: kept as it was`;
    }

    override destroy() {
        this.tabModel?.unregisterWidget(this.viewModel?.id, this);
        XH.safeDestroy(this.content);
        super.destroy();
    }

    //------------------------
    // Implementation
    //------------------------
    /** Rebuild the content when the saved state no longer matches it - a no-op for the widget's own changes. */
    private syncWithViewState() {
        const {source = null, spec = null}: ResearchWidgetState = this.viewModel.viewState ?? {},
            {content} = this,
            sameSource = sameJson(source, this.source),
            sameSpec = !content || !spec || sameJson(spec, content.specModel.spec);

        if (sameSource && sameSpec && (content || !source)) return;
        this.rebuild(source, spec);
    }

    @action
    private rebuild(source: ResearchDataSource, spec: GridSpec | ChartSpec) {
        XH.safeDestroy(this.content);
        this.source = source;
        this.content = null;
        this.problem = null;
        this.dataset = XH.researchService.getDataset(source?.dataset);

        if (!source) return;
        if (!this.dataset) {
            this.problem = `The dataset '${source.dataset}' is not available.`;
            return;
        }

        this.content = this.kind === 'grid' ? this.createGrid(source, spec as GridSpec) : this.createChart(source, spec as ChartSpec);
    }

    private createGrid(source: ResearchDataSource, initialSpec: GridSpec): ConfigurableGridModel {
        const {dataset} = this,
            catalog = fieldCatalogFor(dataset, source);

        return new ConfigurableGridModel({
            title: this.viewModel.title,
            catalog,
            capabilities: researchGridCapabilities,
            defaultSpec: defaultGridSpec(dataset, source),
            initialSpec,
            persistCallback: spec => this.persistSpec(spec),
            gridConfig: {
                store: storeConfigFor(catalog),
                autosizeOptions: {mode: 'onDemand'},
                emptyText: 'No data'
            },
            baseColumn: source.transpose ? field => this.transposedColumn(field) : undefined
        });
    }

    private createChart(source: ResearchDataSource, initialSpec: ChartSpec): ConfigurableChartModel {
        const {dataset} = this,
            defaultSpec = defaultChartSpec(dataset, source);

        if (!defaultSpec) {
            runInAction(() => (this.problem = `${dataset.title} has no numeric fields to chart - show it as a grid instead.`));
            return null;
        }

        return new ConfigurableChartModel({
            title: this.viewModel.title,
            catalog: fieldCatalogFor(dataset, source),
            capabilities: chartCapabilitiesFor(source),
            defaultSpec,
            initialSpec,
            persistCallback: spec => this.persistSpec(spec)
        });
    }

    /** In a transposed grid each row is a field of the dataset - its value is formatted by that field's unit. */
    private transposedColumn(field: string): ColumnSpec {
        if (field !== 'value') return {field};
        return {
            field,
            renderer: (v, {record}) => {
                const datasetField = this.dataset.fields.find(f => f.name === record.raw.name);
                return datasetField ? formatValue(v, valueFormat(datasetField, v)) : v;
            }
        };
    }

    private persistSpec(spec: GridSpec | ChartSpec) {
        this.viewModel.setViewStateKey('spec', spec);
    }
}

/** Equality of saved state - a state read back from a saved dashboard has lost its undefined properties. */
function sameJson(a: any, b: any): boolean {
    return a === b || isEqual(toJson(a), toJson(b));
}

function toJson(v: any): any {
    return v == null ? null : JSON.parse(JSON.stringify(v));
}
